/**
 * スタイリングカルーセル機能
 */
class StylingCarousel {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('[data-slider]');
    this.prevBtn = container.querySelector('[data-slider-prev]');
    this.nextBtn = container.querySelector('[data-slider-next]');
    this.items = container.querySelectorAll('.styling-carousel__item');

    // モーダルはコンテナの親要素から探す
    const section = container.closest('section') || container.parentElement;
    this.modal = section ? section.querySelector('.styling-modal') : null;
    this.modalTriggers = container.querySelectorAll('.styling-carousel__modal-trigger');

    if (this.items.length === 0) return;

    this.currentIndex = 0;
    this.itemsPerView = this.getItemsPerView();
    this.maxIndex = Math.max(0, this.items.length - this.itemsPerView);

    this.init();
  }

  init() {
    this.bindEvents();
    this.bindModalEvents();

    // スライダー機能が有効な場合のみ実行
    if (this.slider) {
      this.updateNavButtons();
      this.handleResize();
    }
  }

  bindEvents() {
    // スライダー機能が有効な場合のみイベントバインド
    if (this.slider) {
      // ナビゲーションボタン
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // キーボード操作
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      });

      // リサイズ対応
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.handleResize();
        }, 250);
      });

      // タッチ・スワイプ対応
      this.addTouchSupport();
    }
  }

  getItemsPerView() {
    const columns = getComputedStyle(this.container).getPropertyValue('--columns-desktop');
    const mobileColumns = getComputedStyle(this.container).getPropertyValue('--columns-mobile');

    if (window.innerWidth >= 990) {
      return parseInt(columns) || 4;
    } else if (window.innerWidth >= 750) {
      return 3;
    } else {
      return parseInt(mobileColumns) || 2;
    }
  }

  handleResize() {
    const newItemsPerView = this.getItemsPerView();

    if (newItemsPerView !== this.itemsPerView) {
      this.itemsPerView = newItemsPerView;
      this.maxIndex = Math.max(0, this.items.length - this.itemsPerView);

      // 現在のインデックスが範囲外の場合は調整
      if (this.currentIndex > this.maxIndex) {
        this.currentIndex = this.maxIndex;
      }

      this.updateSliderPosition();
      this.updateNavButtons();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSliderPosition();
      this.updateNavButtons();
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateSliderPosition();
      this.updateNavButtons();
    }
  }

  goToSlide(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.updateSliderPosition();
    this.updateNavButtons();
  }

  updateSliderPosition() {
    if (!this.slider || this.items.length === 0) return;

    const itemWidth = this.items[0].offsetWidth;
    const gap = this.getGapSize();
    const translateX = -(this.currentIndex * (itemWidth + gap));

    this.slider.style.transform = `translateX(${translateX}px)`;
  }

  getGapSize() {
    if (window.innerWidth >= 990) {
      return 32; // 2rem
    } else if (window.innerWidth >= 750) {
      return 24; // 1.5rem
    } else {
      return 16; // 1rem
    }
  }

  updateNavButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
      this.prevBtn.setAttribute('aria-disabled', this.currentIndex === 0);
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
      this.nextBtn.setAttribute('aria-disabled', this.currentIndex >= this.maxIndex);
    }
  }

  addTouchSupport() {
    if (!this.slider) return;

    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let startTransform = 0;

    this.slider.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
      const transform = this.slider.style.transform;
      startTransform = transform ? parseFloat(transform.replace('translateX(', '').replace('px)', '')) : 0;
    }, { passive: true });

    this.slider.addEventListener('touchmove', (e) => {
      if (!isDragging) return;

      currentX = e.touches[0].clientX;
      const diffX = currentX - startX;
      const newTransform = startTransform + diffX;

      this.slider.style.transform = `translateX(${newTransform}px)`;
    }, { passive: true });

    this.slider.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;

      const diffX = currentX - startX;
      const threshold = 50;

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          this.prev();
        } else {
          this.next();
        }
      } else {
        this.updateSliderPosition();
      }
    }, { passive: true });

    // マウスドラッグ対応
    let isMouseDown = false;

    this.slider.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      isMouseDown = true;
      const transform = this.slider.style.transform;
      startTransform = transform ? parseFloat(transform.replace('translateX(', '').replace('px)', '')) : 0;
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return;

      currentX = e.clientX;
      const diffX = currentX - startX;
      const newTransform = startTransform + diffX;

      this.slider.style.transform = `translateX(${newTransform}px)`;
    });

    document.addEventListener('mouseup', (e) => {
      if (!isMouseDown) return;
      isMouseDown = false;

      const diffX = currentX - startX;
      const threshold = 50;

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          this.prev();
        } else {
          this.next();
        }
      } else {
        this.updateSliderPosition();
      }
    });
  }

  bindModalEvents() {
    console.log('bindModalEvents called', {
      modal: this.modal,
      triggers: this.modalTriggers.length
    });

    if (!this.modal) {
      console.warn('Modal not found');
      return;
    }

    // モーダルトリガーのクリックイベント
    this.modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Modal trigger clicked');
        this.openModal(trigger);
      });
    });

    // モーダル閉じるボタン
    const closeButtons = this.modal.querySelectorAll('[data-modal-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.closeModal();
      });
    });

    // ESCキーでモーダルを閉じる
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && !this.modal.hasAttribute('aria-hidden')) {
        this.closeModal();
      }
    });
  }

  openModal(trigger) {
    if (!this.modal) return;

    // 画像情報を取得
    const imageSrc = trigger.dataset.imageSrc;
    const imageAlt = trigger.dataset.imageAlt;

    // 商品データを取得
    const productDataElement = trigger.parentElement.querySelector('[data-product-data]');
    let productData = { products: [] };

    if (productDataElement) {
      try {
        productData = JSON.parse(productDataElement.textContent);
        console.log('Product data:', productData);
      } catch (e) {
        console.warn('Product data parsing failed:', e);
      }
    } else {
      console.warn('Product data element not found');
    }

    // データセットからも商品IDを取得してバックアップとして使用
    const products = [];
    for (let i = 1; i <= 3; i++) {
      const productId = trigger.dataset[`product${i}`];
      const productFocal = trigger.dataset[`product${i}Focal`];

      if (productId) {
        products.push({
          id: productId,
          focal_point: productFocal || 'center'
        });
      }
    }

    // 商品データがある場合はそれを使用、なければデータセットから構築
    const finalProducts = productData.products.length > 0 ? productData.products : products;

    console.log('Final products:', finalProducts);

    // モーダル内容を更新
    this.updateModalContent(imageSrc, imageAlt, finalProducts);

    // モーダルを表示
    this.modal.removeAttribute('aria-hidden');
    this.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // フォーカスをモーダルに移動
    const firstFocusable = this.modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  updateModalContent(imageSrc, imageAlt, products) {
    console.log('updateModalContent called with:', { imageSrc, imageAlt, products });

    // メイン画像を更新
    const mainImageContainer = this.modal.querySelector('.styling-modal__main-image');
    if (mainImageContainer && imageSrc) {
      // 有効な商品のみをフィルタリング
      const validProducts = products.filter(product => product && product.id);
      console.log('Valid products:', validProducts);

      // フォーカルポイントのHTMLを生成
      const focalPointsHTML = validProducts.map((product, index) => {
        const position = this.getFocalPointPosition(product.focal_point);
        console.log(`Product ${index + 1} focal point:`, product.focal_point, position);

        return `
          <div class="styling-modal__focal-point"
               data-product-index="${index}"
               style="top: ${position.top}%; left: ${position.left}%;">
            <div class="styling-modal__focal-point-dot"></div>
          </div>
        `;
      }).join('');

      mainImageContainer.innerHTML = `
        <div class="styling-modal__image-wrapper">
          <img src="${imageSrc}" alt="${imageAlt || ''}" class="styling-modal__image">
          ${focalPointsHTML}
        </div>
      `;

      console.log('Generated focal points HTML:', focalPointsHTML);
    }

    // 商品リストを更新
    const productsContainer = this.modal.querySelector('.styling-modal__products-list');
    if (productsContainer) {
      const validProducts = products.filter(product => product && product.id);

      if (validProducts.length > 0) {
        const productsHTML = validProducts.map((product, index) => {
          const price = product.price ? `¥${(product.price / 100).toLocaleString()}` : '';
          const comparePrice = product.compare_at_price && product.compare_at_price > product.price
            ? `¥${(product.compare_at_price / 100).toLocaleString()}`
            : '';

          return `
            <div class="styling-modal__product-item" data-product-index="${index}">
              <div class="styling-modal__product-image">
                <img src="${product.featured_image.url}" alt="${product.featured_image.alt || product.title}" loading="lazy">
              </div>
              <div class="styling-modal__product-info">
                <h4 class="styling-modal__product-title">${product.title}</h4>
                <div class="styling-modal__product-price">
                  ${comparePrice ? `<span class="styling-modal__compare-price">${comparePrice}</span>` : ''}
                  <span class="styling-modal__price">${price}</span>
                </div>
                <div class="styling-modal__product-actions">
                  <a href="${product.url}" class="btn btn--primary styling-modal__view-product">
                    商品を見る
                  </a>
                </div>
              </div>
            </div>
          `;
        }).join('');

        productsContainer.innerHTML = productsHTML;
        console.log('Generated products HTML:', productsHTML);

        // ホバーエフェクトを追加
        this.addFocalPointHoverEffects();
      } else {
        productsContainer.innerHTML = '<p>商品が設定されていません</p>';
      }
    }
  }

  getFocalPointPosition(focalPoint) {
    const positions = {
      'center': { top: 50, left: 50 },
      'top': { top: 20, left: 50 },
      'bottom': { top: 80, left: 50 },
      'left': { top: 50, left: 20 },
      'right': { top: 50, left: 80 },
      'top-left': { top: 20, left: 20 },
      'top-right': { top: 20, left: 80 },
      'bottom-left': { top: 80, left: 20 },
      'bottom-right': { top: 80, left: 80 }
    };

    return positions[focalPoint] || positions['center'];
  }

  addFocalPointHoverEffects() {
    const focalPoints = this.modal.querySelectorAll('.styling-modal__focal-point');
    const productItems = this.modal.querySelectorAll('.styling-modal__product-item');

    // フォーカルポイントにホバーイベントを追加
    focalPoints.forEach(point => {
      const productIndex = point.dataset.productIndex;

      point.addEventListener('mouseenter', () => {
        this.highlightProduct(productIndex, productItems);
      });

      point.addEventListener('mouseleave', () => {
        this.resetProductHighlight(productItems);
      });
    });

    // 商品アイテムにホバーイベントを追加
    productItems.forEach(item => {
      const productIndex = item.dataset.productIndex;

      item.addEventListener('mouseenter', () => {
        this.highlightFocalPoint(productIndex, focalPoints);
      });

      item.addEventListener('mouseleave', () => {
        this.resetFocalPointHighlight(focalPoints);
      });
    });
  }

  highlightProduct(targetIndex, productItems) {
    productItems.forEach(item => {
      if (item.dataset.productIndex === targetIndex) {
        item.classList.add('styling-modal__product-item--highlighted');
      } else {
        item.classList.add('styling-modal__product-item--dimmed');
      }
    });
  }

  resetProductHighlight(productItems) {
    productItems.forEach(item => {
      item.classList.remove('styling-modal__product-item--highlighted', 'styling-modal__product-item--dimmed');
    });
  }

  highlightFocalPoint(targetIndex, focalPoints) {
    focalPoints.forEach(point => {
      if (point.dataset.productIndex === targetIndex) {
        point.classList.add('styling-modal__focal-point--highlighted');
      } else {
        point.classList.add('styling-modal__focal-point--dimmed');
      }
    });
  }

  resetFocalPointHighlight(focalPoints) {
    focalPoints.forEach(point => {
      point.classList.remove('styling-modal__focal-point--highlighted', 'styling-modal__focal-point--dimmed');
    });
  }
}

// 初期化
function initStylingCarousels() {
  const carousels = document.querySelectorAll('.styling-carousel');
  console.log('Found carousels:', carousels.length);
  carousels.forEach((carousel, index) => {
    console.log(`Initializing carousel ${index + 1}`);
    new StylingCarousel(carousel);
  });
}

// DOMContentLoaded後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStylingCarousels);
} else {
  initStylingCarousels();
}

// Shopifyセクション設定変更時の再初期化
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target;
  const carousel = section.querySelector('.styling-carousel');
  if (carousel) {
    new StylingCarousel(carousel);
  }
});

// セクション設定変更時
document.addEventListener('shopify:section:reorder', initStylingCarousels);
document.addEventListener('shopify:section:select', (e) => {
  const section = e.target;
  const carousel = section.querySelector('.styling-carousel');
  if (carousel) {
    // フォーカスを当てる
    carousel.focus();
  }
});
